import type { ReactNode } from 'react'

import { Box } from '@mui/material'

import { Actions } from '@components/shared/Actions'
import { EditIcon } from '@assets/svgs/EditIcon'
import { InfoIcon } from '@assets/svgs/InfoIcon'
import { currencyMask } from '@utils/masks/currencyMask'
import { Tooltip } from '@components/shared/Tooltip'

import type { ColumnDefProps } from '@components/shared/Table/types'
import type { DetailedSummaryV2ItemProps } from '../context/types'
import type { ConsumptionAveragePercentagesProps } from '@services/api/consumption/types'

// Cor de destaque da coluna Prévia Final (RF07)
const FINAL_PREVIEW_HIGHLIGHT = '#EEEFF7'

const CELL_TOOLTIPS: Record<string, Partial<Record<string, string>>> = {
  consumo_cd: {
    realized: 'Aplicado conceito de data corte mês anterior',
    projection:
      'Aplicado conceito de data corte mês atual com base na OC com data prevista para o último dia útil do mês vigente.'
  },
  consumo_pap: {
    projection:
      'Valor referente a OCs com data prevista para o mês vigente, ainda em aberto e sem vínculo com Nota Fiscal.'
  },
  gastos_cd: {
    adjustment: 'Valor calculado sobre o ajuste adicionado ao Consumo CD x Índice'
  },
  logistica: {
    adjustment: 'Valor calculado sobre o ajuste adicionado ao Consumo CD x Índice'
  }
}

const FINAL_PREVIEW_AVERAGE_KEY: Record<string, keyof ConsumptionAveragePercentagesProps> = {
  gastos_cd: 'averageGastoCd',
  logistica: 'averageGastoLogistico',
  cred_piscofins_cd: 'averagePisCofinsCd',
  cred_piscofins_pap: 'averagePisCofinsPap'
}

const infoIconSx = { display: 'flex', alignItems: 'center', color: '#9E9E9E', cursor: 'help' }

function InfoTooltip({ title }: { title: ReactNode }) {
  return (
    <Tooltip title={title} placement="top">
      <Box component="span" sx={infoIconSx}>
        <InfoIcon />
      </Box>
    </Tooltip>
  )
}

const REALIZADO_HEADER = (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
    Realizado
    <InfoTooltip title="O valor apresentado considera Realizado D-1" />
  </Box>
)

const MEDIA_HIST_HEADER = (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
    Média Histórica
    <InfoTooltip
      title={
        <>
          Média Trimestre (M-4 até M-2)
          <br />
          Valor = (SOMA R$ / SOMA DIAS ÚTEIS) * DIAS ÚTEIS MÊS CORRENTE
        </>
      }
    />
  </Box>
)

const FINAL_PREVIEW_HEADER = (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
    Prévia Final
    <InfoTooltip title="A soma desta coluna é o valor considerado na Prévia Consumo" />
  </Box>
)

const DESVIO_HEADER = (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
    Desvio (Prévia vs Média)
    <InfoTooltip title="Valor é igual ao desvio entre a Prévia Final e Média Histórica" />
  </Box>
)

function fmt(value: number | null | undefined): string {
  if (value == null) return '-'
  return currencyMask(value.toFixed(2)) || '-'
}

function fmtColored(value: number | null | undefined): ReactNode {
  const text = fmt(value)
  if (value != null && value < 0)
    return (
      <Box component="span" sx={{ color: '#E00' }}>
        {text}
      </Box>
    )
  return text
}

export function getFinalPreviewAverageTooltip(
  categoryKey: string,
  averagePercentages?: ConsumptionAveragePercentagesProps | null
): string | undefined {
  const averageKey = FINAL_PREVIEW_AVERAGE_KEY[categoryKey]
  const average = averageKey ? averagePercentages?.[averageKey] : null

  if (average == null) return undefined

  return `Média = ${average.toFixed(2).replace('.', ',')}%`
}

function cellValue(content: ReactNode, tooltipText?: string | null): ReactNode {
  return (
    <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {content}
      {tooltipText && (
        <Box sx={{ position: 'absolute', right: 0, display: 'flex', alignItems: 'center' }}>
          <InfoTooltip title={tooltipText} />
        </Box>
      )}
    </Box>
  )
}

interface ParseDetailedSummaryProps {
  data: DetailedSummaryV2ItemProps[]
  actionsDisabled: boolean
  averagePercentages?: ConsumptionAveragePercentagesProps | null
  onEdit: (data: DetailedSummaryV2ItemProps) => void
}

export const parseDetailedSummaryGrid = ({ data, averagePercentages, onEdit }: ParseDetailedSummaryProps) => {
  if (!data) return []

  const parsedData: ColumnDefProps[][] = data.map((item) => {
    // RF04/RN01: Desvio = Prévia Final – Média Histórica
    const deviation = item.historicalAverage != null ? item.finalPreview - item.historicalAverage : null

    const realizedTooltip = CELL_TOOLTIPS[item.categoryKey]?.realized
    const projectionTooltip = CELL_TOOLTIPS[item.categoryKey]?.projection
    const adjustmentTooltip = CELL_TOOLTIPS[item.categoryKey]?.adjustment
    const finalPreviewTooltip = getFinalPreviewAverageTooltip(item.categoryKey, averagePercentages)

    return [
      {
        id: 'description',
        columnName: 'Categorias',
        value: item.description || '-'
      },
      {
        id: 'realized',
        columnName: REALIZADO_HEADER,
        align: 'center',
        withoutTooltip: true,
        value: cellValue(fmtColored(item.realized), realizedTooltip)
      },
      {
        id: 'projection',
        columnName: 'Projeção',
        align: 'center',
        withoutTooltip: true,
        value: cellValue(fmtColored(item.projection), projectionTooltip)
      },
      {
        id: 'adjustment',
        columnName: 'Ajuste',
        align: 'center',
        withoutTooltip: true,
        value: cellValue(fmtColored(item.adjustment), adjustmentTooltip)
      },
      {
        id: 'finalPreview',
        columnName: FINAL_PREVIEW_HEADER,
        align: 'center',
        withoutTooltip: true,
        bodyColumnColor: FINAL_PREVIEW_HIGHLIGHT,
        value: cellValue(fmtColored(item.finalPreview), finalPreviewTooltip)
      },
      {
        id: 'historicalAverage',
        columnName: MEDIA_HIST_HEADER,
        align: 'center',
        withoutTooltip: true,
        value: cellValue(fmtColored(item.historicalAverage))
      },
      {
        id: 'deviationVsAverage',
        columnName: DESVIO_HEADER,
        align: 'center',
        withoutTooltip: true,
        value: cellValue(fmtColored(deviation))
      },
      {
        id: 'action',
        columnName: 'Ações',
        align: 'center',
        minWidth: '5rem',
        maxWidth: '5rem',
        stickyPosition: 'right',
        withoutTooltip: true,
        value: (
          <Actions
            actions={[{ label: 'Editar', icon: <EditIcon />, onClick: () => onEdit(item) }]}
            disabled={item.allowEdit === false}
          />
        )
      }
    ]
  })

  return parsedData
}
